"use client";

import { DrawerPreview as Drawer } from "@base-ui/react/drawer";
import type { ReactNode } from "react";

import { CtaButton } from "../content/cta-button";
import type { CtaAction } from "../content/cta-button";
import {
  RoomDetailContent,
  RoomDetailContentSkeleton,
} from "../content/room-detail-content";
import type { RoomDetailView } from "../content/room-detail-content";

/** sr-only の Drawer.Title。ローディング / エラー / 本体で共通利用する。 */
function DrawerSrTitle({ text }: { text: string }) {
  return (
    <Drawer.Title render={<span />} className="sr-only">
      {text}
    </Drawer.Title>
  );
}

function RoomDetailError() {
  return (
    <div
      className="bg-dx-surface-base flex min-h-[200px] items-center justify-center px-[16px] py-[40px]"
      role="alert"
    >
      <p className="font-notojp text-dx-text-default text-[14px] leading-[1.6]">
        部屋情報を取得できませんでした。時間をおいて再度お試しください。
      </p>
    </div>
  );
}

/**
 * loading → skeleton / error → RoomDetailError / 成功 → DrawerSrTitle + RoomDetailContent
 * の 3 状態スキャフォルドを共通化したフレームコンポーネント。
 *
 * - `loadingAction`: loading 中に出す CTA（accommodation 経路の予約 CTA loading 表示用）。
 * - `primaryAction`: 成功時の CTA（accommodation / close 用）。
 * - `inlineContentSlot`: roomType 経路の予約カレンダー等。指定時は `primaryAction` より優先する。
 */
export function BodyFrame({
  loading,
  error,
  view,
  primaryAction,
  loadingAction,
  inlineContentSlot,
}: {
  loading: boolean;
  error: boolean;
  view: RoomDetailView | null;
  primaryAction?: CtaAction;
  loadingAction?: CtaAction;
  inlineContentSlot?: ReactNode;
}) {
  if (loading) {
    return (
      <>
        <DrawerSrTitle text="部屋詳細" />
        <RoomDetailContentSkeleton />
        {loadingAction && <CtaButton action={loadingAction} />}
      </>
    );
  }
  if (error || !view) {
    return (
      <>
        <DrawerSrTitle text="部屋詳細" />
        <RoomDetailError />
      </>
    );
  }
  return (
    <>
      <DrawerSrTitle text={`${view.bigTitle} の部屋詳細`} />
      <RoomDetailContent
        view={view}
        primaryAction={primaryAction}
        inlineContentSlot={inlineContentSlot}
      />
    </>
  );
}
